import {
  ApplicationConfig,
  LOCALE_ID,
  inject,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import { es_ES, provideNzI18n } from 'ng-zorro-antd/i18n';
import { registerLocaleData } from '@angular/common';
import es from '@angular/common/locales/es';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { NZ_ICONS } from 'ng-zorro-antd/icon';
import {
  // ── Navegación y layout ──────────────────────────────────────────────
  UserOutline,
  LockOutline,
  MenuUnfoldOutline,
  MenuFoldOutline,
  HomeOutline,
  LogoutOutline,
  LeftOutline,
  RightOutline,
  ArrowLeftOutline,
  // ── Empleados y contactos ────────────────────────────────────────────
  ContactsOutline,
  TeamOutline,
  UserAddOutline,
  // ── Acciones CRUD ────────────────────────────────────────────────────
  EyeOutline,
  EditOutline,
  DeleteOutline,
  PlusOutline,
  PlusCircleOutline,
  CloseOutline,
  CloseCircleOutline,
  CloseCircleFill,
  SelectOutline,
  SyncOutline,
  ReloadOutline,
  // ── Búsqueda y filtrado ──────────────────────────────────────────────
  SearchOutline,
  // ── Estado / feedback ────────────────────────────────────────────────
  CheckOutline,
  ClearOutline,
  CheckCircleOutline,
  CheckCircleFill,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  CheckSquareOutline,
  InfoCircleOutline,
  WarningOutline,
  StopOutline,
  // Icono por defecto de <nz-popconfirm> (lo pinta con nzTheme="fill").
  // Sin registrarlo, el popconfirm sale sin icono.
  ExclamationCircleFill,
  // ── Vacaciones y calendario ──────────────────────────────────────────
  CalendarOutline,
  CarryOutOutline,
  ScheduleOutline,
  HistoryOutline,
  ClockCircleOutline,
  HourglassOutline,
  // ── Aprobaciones ─────────────────────────────────────────────────────
  AuditOutline,
  LikeOutline,
  // ── Archivos y exportación ───────────────────────────────────────────
  FileTextOutline,
  FileOutline,
  FilePdfOutline,
  FileExcelOutline,
  DownloadOutline,
  ExportOutline,
  CloudUploadOutline,
  UploadOutline,
  FolderOpenOutline,
  // ── Mensajes y notificaciones ────────────────────────────────────────
  MessageOutline,
  BellOutline,
  // ── Dashboard / gráficas ─────────────────────────────────────────────
  DashboardOutline,
  BarChartOutline,
  LineChartOutline,
  PieChartOutline,
  SmileOutline,
  InboxOutline,
  // ── Organización / jerarquía ─────────────────────────────────────────
  ApartmentOutline,
  PartitionOutline,
  CrownOutline,
  // ── Misc ─────────────────────────────────────────────────────────────
  LoadingOutline,
  SaveOutline,
  SwapOutline,
  CaretUpOutline,
  CaretDownOutline,
  DragOutline,
  HolderOutline,
  FireOutline,
  StarFill,
} from '@ant-design/icons-angular/icons';
import { credentialsInterceptor } from './interceptors/credentials.interceptor';
import { environment } from 'src/environments/environment';
import { proveerSesion, sesionExpiradaInterceptor } from '@intelishs-group/session-core';
import { NotifyService } from '@intelishs-group/notify-service';

registerLocaleData(es);

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    // `credentialsInterceptor` es el LOCAL y no el de la librería, a propósito:
    // el de la librería pone `withCredentials` en TODAS las peticiones y éste
    // sólo en los orígenes del ecosistema. Una petición con credenciales a un
    // tercero que responda `Access-Control-Allow-Origin: *` falla por CORS, no
    // por auth, y el síntoma no señala la causa. `SesionService` no depende de
    // él: pone `withCredentials` explícito en todas sus llamadas.
    provideHttpClient(withInterceptors([credentialsInterceptor, sesionExpiradaInterceptor])),
    provideBrowserGlobalErrorListeners(),
    // El locale `es` de ng-zorro trae `items_per_page: '/ página'`, así que el
    // selector de tamaño decía "10 / página". El `/` está haciendo de "por", y
    // ese atajo se lee mal: "10 por página" lo dice sin que el lector tenga que
    // interpretar el símbolo. (Y no "páginas": el `/` significa "por", así que el
    // plural quedaría en desacuerdo — son 10 por UNA página.)
    provideNzI18n({
      ...es_ES,
      Pagination: { ...es_ES.Pagination, items_per_page: 'por página' },
    }),
    { provide: LOCALE_ID, useValue: 'es' },

    {
      provide: NZ_ICONS,
      useValue: [
        // Navegación y layout
        UserOutline,
        LockOutline,
        MenuUnfoldOutline,
        MenuFoldOutline,
        HomeOutline,
        LogoutOutline,
        LeftOutline,
        RightOutline,
        ArrowLeftOutline,
        // Empleados y contactos
        ContactsOutline,
        TeamOutline,
        UserAddOutline,
        // Acciones CRUD
        EyeOutline,
        EditOutline,
        DeleteOutline,
        PlusOutline,
        PlusCircleOutline,
        CloseOutline,
        CloseCircleOutline,
        CloseCircleFill,
        SelectOutline,
        SyncOutline,
        ReloadOutline,
        // Búsqueda
        SearchOutline,
        // Estado / feedback
        CheckCircleOutline,
        CheckCircleFill,
        CheckCircleTwoTone,
        CloseCircleTwoTone,
        CheckSquareOutline,
        InfoCircleOutline,
        WarningOutline,
        StopOutline,
        ExclamationCircleFill,
        // Vacaciones y calendario
        CalendarOutline,
        CarryOutOutline,
        ScheduleOutline,
        HistoryOutline,
        ClockCircleOutline,
        HourglassOutline,
        // Aprobaciones
        AuditOutline,
        LikeOutline,
        // Archivos y exportación
        FileTextOutline,
        FileOutline,
        FilePdfOutline,
        FileExcelOutline,
        DownloadOutline,
        ExportOutline,
        CloudUploadOutline,
        UploadOutline,
        FolderOpenOutline,
        // Mensajes y notificaciones
        MessageOutline,
        BellOutline,
        // Dashboard / gráficas
        DashboardOutline,
        BarChartOutline,
        LineChartOutline,
        PieChartOutline,
        SmileOutline,
        InboxOutline,
        // Organización / jerarquía
        ApartmentOutline,
        PartitionOutline,
        CrownOutline,
        // Misc
        LoadingOutline,
        SaveOutline,
        SwapOutline,
        CaretUpOutline,
        CaretDownOutline,
        DragOutline,
        HolderOutline,
        FireOutline,
        StarFill,
      ],
    },

    // `LAYOUT_ENV` se fue con `<layout-main>`: lo pedían el shell anterior y su
    // `MenuService`. El shell nuevo no lee nada del inyector y el nombre del
    // sistema entra por `[marca]`.

    // ── La sesión de PLATAFORMA ──────────────────────────────────────────
    //
    // Sustituye a `provideSession({ tokenName })` MÁS las 330 líneas de
    // `session-bootstrap.ts` y `session-fallback.service.ts`, que pedían
    // `/api/Membership/GetTokenInSession`, decodificaban el JWT a mano y
    // reconstruían el usuario claim a claim.
    //
    // Ya no hay JWT que decodificar: las cookies son `HttpOnly` y la aplicación
    // nunca las ve. La sesión se PREGUNTA al servicio de identidad, y se resuelve
    // ANTES de montar el primer componente — así no hay pantalla que se dibuje
    // con datos vacíos y desaparezca.
    proveerSesion({
      identidadUrl: environment.identidadUrl,
      loginUrl: environment.loginUrl || environment.logoutUrl,
      // Obligatorio en la librería, y no por gusto: su primera versión redirigía
      // en silencio y se comió un aviso que llevaba años en producción.
      avisarSesionCaducada: () => inject(NotifyService).unauthorizedNotify(),
    }),
  ],
};
